import React, { useEffect, useMemo } from 'react'
import { getDeliveryQuote } from 'apis/vehicleApis'
import { SlideOutDropdown } from 'components/SlideOut/SlideOutDropdown'
import { useState } from 'react'
import { useBiddingSidePanel } from 'store/useBiddingSidePanelStore'
import { useVehicleStore } from 'store/useVehicleStore'
import { IDropdownData, IOption } from 'types/baseTypes'
import { DeliveryInformation, IAddress, IDeliveryQuote } from 'types/vehicleTypes'
import readyAutoImage from 'images/ReadyLogistics.png'
import { AddressModal } from 'components/Grounding/AddressModal'
import { DeliveryChargeCard } from 'components/DeliveryChargeCard/DeliveryChargeCard'
import { StyledLoader, StyledModal } from 'components/DeliveryChargeCard/StyledModel'
import {
  AddressTypes,
  DELIVERY_INFORMATION_NOT_AVAILABLE,
  READY_LOGISTIC_TRANSPORT_ID,
  READY_TOOLTIP
} from 'common/constants'
import { useFormikContext } from 'formik'
import { Loader } from 'components/Loader'
import shallow from 'zustand/shallow'
import { saveAddress } from 'apis/userApis'

interface IProps {
  isAddressModalOpen: boolean
  onOpenAddressModal: (isOpen: boolean) => void
}
const TransportationPanelComponent = ({ isAddressModalOpen, onOpenAddressModal }: IProps) => {
  // Zustand state
  const {
    biddingVehicleInstanceId,
    deliveryInformation,
    setDeliveryInformation,
    otherChildId,
    setOtherChildId,
    selectedDeliveryPrice
  } = useBiddingSidePanel(
    (state) => ({
      biddingVehicleInstanceId: state.biddingVehicleInstanceId,
      deliveryInformation: state.deliveryInformation,
      setDeliveryInformation: state.setDeliveryInformation,
      otherChildId: state.otherChildId,
      setOtherChildId: state.setOtherChildId,
      selectedDeliveryPrice: state.selectedDeliveryPrice
    }),
    shallow
  )

  const vehicles = useVehicleStore((state) => state.vehicles)
  const biddingVehicle = React.useMemo(
    () => vehicles?.find((vehicleData) => vehicleData.vehicle.InstanceID === biddingVehicleInstanceId),
    [vehicles, biddingVehicleInstanceId]
  )

  // Derived state from props and global state
  const {
    // VehicleDeliveryQuotationsInfors,
    VehicleDeliveryQuotations,
    HasQuote,
    DeliveryAddresses,
    TransportProviders,
    AddressID,
    Error
  } = deliveryInformation || {}

  const readyAutoQuote = useMemo(() => VehicleDeliveryQuotations, [VehicleDeliveryQuotations])

  const isExistQuote = useMemo(() => HasQuote, [HasQuote])

  const readyAutoProvider = useMemo(
    () => TransportProviders?.find((provider) => provider.ID === READY_LOGISTIC_TRANSPORT_ID),
    [TransportProviders]
  )

  // Local state
  const [isLocationActionsVisible, setIsLocationActionsVisible] = useState(true)
  const [isSaveAddressToBuyer, setIsSaveAddressToBuyer] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const formik = useFormikContext<{ BiddingDealerID: number; DeliveryAddressID: number }>()
  const [addresses, setAddresses] = useState<IOption<IDropdownData>[]>([])

  // Effect to synchronize addresses from global state to local state
  useEffect(() => {
    setAddresses(
      DeliveryAddresses?.filter((address) => address.ID !== 0 && address.ID !== -1).map((address) => ({
        item: address,
        label: address.Description,
        value: address.ID
      })) || []
    )
  }, [DeliveryAddresses])

  // Effect to synchronize selected address with Formik
  useEffect(() => {
    formik.setFieldValue('DeliveryAddressID', AddressID || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AddressID])

  // Effect to ensure AddressModal is closed when component mounts
  useEffect(() => {
    onOpenAddressModal(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Effect to select the applied quote when the component loads
  useEffect(() => {
    if (VehicleDeliveryQuotations) {
      setOtherChildId(1)
    }
    // Only run on initial load of quotes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VehicleDeliveryQuotations])

  const getQuoteForAddress = async (addressId: number, currentDeliveryInfo: DeliveryInformation) => {
    setOtherChildId(null)

    const quoteRequest: IDeliveryQuote = {
      ChannelID: biddingVehicle?.vehicle.SaleChannelID,
      InterestID: biddingVehicle?.vehicle.OfferID,
      InterestStatusID: biddingVehicle?.vehicle.OfferStatusID,
      LocationID: currentDeliveryInfo.LocationID,
      PaymentTypeID: 0,
      SalePrice: biddingVehicle?.auctionInfo.NextBidAmount || biddingVehicle?.vehicle.FinalPrice,
      SalesSessionStepId: biddingVehicle?.vehicle.SalesSessionStepID,
      VehicleInstanceID: biddingVehicle?.vehicle.InstanceID,
      AddressID: addressId,
      PickUpAddressId: currentDeliveryInfo.PickupAddressId,
      PickUpZIP: currentDeliveryInfo.PickUpZIP,
      DropOffAddressId: addressId,
      DropOffZIP: null //need to update
    }

    try {
      setIsLoading(true)
      const deliveryQuote = await getDeliveryQuote(quoteRequest)
      setDeliveryInformation({
        ...currentDeliveryInfo,
        HasQuote: deliveryQuote.HasQuote,
        VehicleDeliveryQuotations: deliveryQuote.VehicleDeliveryQuotations,
        DeliveryErrors: deliveryQuote.DeliveryErrors,
        AddressID: addressId,
        Error: deliveryQuote.Error
      })
      setOtherChildId(null)
    } catch (error) {
      console.error('Error fetching delivery quote:', error)
      setDeliveryInformation({
        ...currentDeliveryInfo,
        AddressID: addressId,
        Error: error.Message
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenAddressModal = () => {
    onOpenAddressModal(true)
    setIsLocationActionsVisible(false)
  }

  const handleDeliveryAddressChange = async (selectedOption: IOption<IDropdownData> | number) => {
    let addressId: number

    if (typeof selectedOption === 'number') {
      addressId = selectedOption
    } else {
      addressId = selectedOption.value
    }
    await getQuoteForAddress(addressId, deliveryInformation)
  }

  const handleSaveAddress = async (address: IAddress) => {
    try {
      onOpenAddressModal(false)
      setIsLoading(true)

      const currentBuyerId = formik.values.BiddingDealerID
      const result = await saveAddress({
        Address: address,
        BuyerId: currentBuyerId,
        SaveToBuyer: isSaveAddressToBuyer
      })

      if (result > 0 && deliveryInformation) {
        const newAddress = {
          ID: result,
          Description: `${address.AddressLineOne}${address.AddressLineTwo ? `, ${address.AddressLineTwo}` : ''}, ${
            address.Town
          }, ${deliveryInformation?.States?.find((state) => state.ID === Number(address.StateId))?.Code || ''} ${
            address.PostCode
          }`,
          FieldDescription:
            deliveryInformation?.States?.find((state) => state.ID === Number(address.StateId))?.Code || ''
        }

        const newOption = {
          item: newAddress,
          label: newAddress.Description,
          value: newAddress.ID
        }

        setAddresses((prev) => [...prev, newOption])

        const updatedDeliveryInfo = {
          ...deliveryInformation,
          DeliveryAddresses: [...(deliveryInformation.DeliveryAddresses || []), newAddress]
        }
        await getQuoteForAddress(newOption.value, updatedDeliveryInfo)
        formik.setFieldValue('DeliveryAddressID', newOption.value)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLocationActionsVisible(true)
    }
  }

  const handleDeliveryOptionChange = (price: number | null, transportProviderId?: number) => {
    if (price !== null && deliveryInformation) {
      setOtherChildId(transportProviderId)
      setDeliveryInformation({
        ...deliveryInformation,
        HasQuote: true,
        VehicleDeliveryQuotations: deliveryInformation?.VehicleDeliveryQuotations,
        DeliveryErrors: deliveryInformation?.DeliveryErrors,
        AddressID: formik.values['DeliveryAddressID'] || deliveryInformation?.AddressID
      })
    }
  }

  return (
    <StyledModal>
      {isLoading && (
        <StyledLoader>
          <Loader />
        </StyledLoader>
      )}
      <h2>Transportation Quote</h2>

      {readyAutoProvider && (
        <DeliveryChargeCard
          imageSrc={readyAutoImage}
          altText="Ready Auto"
          quotePrice={readyAutoQuote?.DiscountedPrice || selectedDeliveryPrice || null}
          errorMessage={
            !!Error || (isExistQuote && !readyAutoQuote && !selectedDeliveryPrice)
              ? DELIVERY_INFORMATION_NOT_AVAILABLE
              : null
          }
          disabled={!!Error || !isExistQuote || (isExistQuote && !readyAutoQuote && !selectedDeliveryPrice)}
          radioName="deliveryOption"
          radioValue="deliveryCharge1"
          radioId="deliveryCharge1"
          isLoading={isLoading}
          checked={otherChildId === READY_LOGISTIC_TRANSPORT_ID}
          toolTipText={READY_TOOLTIP}
          onChange={() => handleDeliveryOptionChange(readyAutoQuote?.DiscountedPrice, READY_LOGISTIC_TRANSPORT_ID)}
        />
      )}

      <SlideOutDropdown
        required
        label="Delivery Address"
        options={addresses}
        fallbackValue={deliveryInformation?.AddressID || addresses?.[0]}
        name="DeliveryAddressID"
        onChange={(selectedOption) => handleDeliveryAddressChange(selectedOption)}
      ></SlideOutDropdown>
      {isLocationActionsVisible && (
        <div className="location-actions">
          <span
            style={{
              cursor: 'pointer'
            }}
            onClick={handleOpenAddressModal}
          >
            Add New Location
          </span>
        </div>
      )}
      {isAddressModalOpen && (
        <AddressModal
          show={isAddressModalOpen}
          defaultAddressDetail={{
            ID: 0,
            Address1: '',
            Address2: '',
            City: '',
            Zip: '',
            StateCode: '-1',
            CountryCode: deliveryInformation?.DefaultCountry?.toString() || '-1',
            AddressTypeID: 0
          }}
          deliveryInformation={deliveryInformation}
          isModal={false}
          addressTypeId={AddressTypes.DELIVERY}
          events={{
            onClose: () => {
              onOpenAddressModal(false)
              setIsLocationActionsVisible(true)
            },
            handleSaveAddress: handleSaveAddress,
            setIsSaveAddressToBuyer: setIsSaveAddressToBuyer
          }}
        />
      )}
    </StyledModal>
  )
}

export const TransportationPanel = React.memo(TransportationPanelComponent)
